import { ActionContext, ActionTree } from "vuex";
import { IPaymentState } from "@/core/interfaces/state/paymentState.interface";
import PaymentsService from "@/modules/Payments/services/payments.service";
import {
  IPayment,
  IPayments,
} from "@/core/interfaces/state/payments.interface";
import { FetchPaymentsParamsRequestModel } from "@/modules/Payments/models/fetchPaymentsParams.request";
import { CreatePaymentBodyRequest } from "@/modules/Payments/models/createPaymentBody.request";
import { UpdateBankTransferRequestModel } from "@/modules/Payments/models/updateBankTransfer.request";
import {
  ContentUpload,
  StartDateEndDateDTO,
} from "@/modules/Organization/dtos/organization.dto";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";

import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { StoreState } from "@/store/type";
import { AUTH_GETTERS } from "@/modules/Auth/vuex/getters";
import { USER_ROLES } from "@/core/enums/USER_ROLES";

const actions: ActionTree<IPaymentState, StoreState> = {
  async fetchPayments(
    context: ActionContext<IPaymentState, StoreState>,
    params: FetchPaymentsParamsRequestModel,
  ) {
    context.commit("enableIsFetchingPayments");
    const paymentsService = new PaymentsService();
    const response: IPayments = await paymentsService.fetchPayments(params);
    context.commit("savePayments", response);
  },

  async fetchPayment(
    context: ActionContext<IPaymentState, StoreState>,
    id: string,
  ) {
    context.commit("enableIsFetchingPayment");
    const paymentsService = new PaymentsService();
    const response: IPayment | null = await paymentsService.fetchPayment(id);
    context.commit("savePayment", response);
  },

  async getPaymentList(
    context: ActionContext<IPaymentState, StoreState>,
    organization_id: string,
  ) {
    const paymentsService = new PaymentsService();
    const response: ICoreResponse =
      await paymentsService.getAllPaymentMethods(organization_id);
    try {
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setPaymentMethods", response.data?.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setSectorStatsLoading", false);
    }
    notificationService.triggerNotification();
  },
  async createPayment(
    context: ActionContext<IPaymentState, StoreState>,
    {
      payload,
      callback,
    }: { payload: CreatePaymentBodyRequest; callback: (id: string) => void },
  ) {
    context.commit("enableIsCreatingPayment");
    const paymentsService = new PaymentsService();
    await paymentsService.createPayment(payload, callback);
    context.commit("disableIsCreatingPayment");
  },
  async fetchPaymentsBySector(
    context: ActionContext<IPaymentState, StoreState>,
    payload: StartDateEndDateDTO,
  ) {
    context.commit("setPaymentsByMethods", {});
    try {
      context.commit("enableIsFetchingPayments");
      const paymentsService = new PaymentsService();
      const response: ICoreResponse =
        await paymentsService.getAllPaymentsByDate(payload);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setPaymentsByMethods", response.data?.data);
      } else {
        throw response;
      }
    } catch (err) {
      context.commit("setPaymentsByMethods", null);
    } finally {
      context.commit("disbaleIsFetchingPayments");
    }
  },
  async paymentsUploadContent(
    context: ActionContext<IPaymentState, StoreState>,
    {
      payload,
      financialProfilePublicId,
    }: { payload: ContentUpload; financialProfilePublicId: string },
  ) {
    let public_id: string | null = null;
    let content_url: string | null = null;
    let response: ICoreResponse = {
      success: false,
    };

    try {
      const paymentsService = new PaymentsService();

      response = await paymentsService.paymentContentUpload(
        payload,
        financialProfilePublicId,
      );

      if (response.success && response.status === StatusCodes.OK) {
        public_id = response.data?.data.public_id;
        content_url = response.data?.data.content_url;

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = response.data?.message;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }

    notificationService.triggerNotification();

    return [public_id, content_url];
  },
  async updateBankTransfer(
    context: ActionContext<IPaymentState, StoreState>,
    {
      paymentId,
      payload,
      callback,
    }: {
      paymentId: string;
      payload: UpdateBankTransferRequestModel;
      callback?: () => void;
    },
  ) {
    context.commit("enableIsUpdatingBankTransfer");
    const paymentsService = new PaymentsService();
    try {
      const response = await paymentsService.updateBankTransfer(
        paymentId,
        payload,
      );
      if (response.success) {
        await context.dispatch("fetchPayment", paymentId);
        if (callback) {
          callback();
        }
      }
    } catch (error) {
      console.error("Error updating bank transfer:", error);
    } finally {
      context.commit("disableIsUpdatingBankTransfer");
    }
  },
  async fetchSystemBanks(
    context: ActionContext<IPaymentState, StoreState>,
    payload?: { paymentId?: string },
  ) {
    context.commit("enableIsFetchingSystemBanks");
    const paymentsService = new PaymentsService();
    const userRole = context.rootGetters[AUTH_GETTERS.USER_ROLE];
    const isFinanceUser = userRole === USER_ROLES.FINANCE;
    try {
      const response: ICoreResponse = await paymentsService.fetchSystemBanks(
        payload?.paymentId,
        isFinanceUser,
      );
      if (response.success && response.data) {
        const banksData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data || [];
        context.commit("saveSystemBanks", banksData);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
        context.commit("saveSystemBanks", []);
      }
    } catch (error) {
      console.error("Error fetching system banks:", error);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      context.commit("saveSystemBanks", []);
    } finally {
      context.commit("disableIsFetchingSystemBanks");
    }
  },
};

export default actions;
